import * as APITypes from "~/API";
import type { TransactionListFiltersType } from "~/types";

const PAYMENT_STATUSES = new Set<string>(
  Object.values(APITypes.PaymentStatusType),
);
const TRANSACTION_REASONS = new Set<string>(
  Object.values(APITypes.TransactionReason),
);

export const DEFAULT_TRANSACTION_STATUSES: APITypes.PaymentStatusType[] = [
  APITypes.PaymentStatusType.Pending,
  APITypes.PaymentStatusType.Processing,
];
export const DEFAULT_TRANSACTION_TABLE_LIMIT = 300;
export const TRANSACTION_TABLE_LOAD_MORE_INCREMENT = 100;

function trimId(value: string | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function parseOptionalInt(
  value: string | null,
  min: number,
  max: number,
): number | undefined {
  if (value == null || value === "") return undefined;
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < min || parsed > max) {
    return undefined;
  }
  return parsed;
}

function parseStatuses(
  searchParams: URLSearchParams,
): APITypes.PaymentStatusType[] | undefined {
  if (searchParams.has("statuses")) {
    const statusesRaw = searchParams.get("statuses") ?? "";
    const parsed = statusesRaw
      .split(",")
      .map((value) => value.trim())
      .filter((value) => PAYMENT_STATUSES.has(value)) as APITypes.PaymentStatusType[];
    return parsed.length > 0 ? parsed : undefined;
  }

  const statusRaw = searchParams.get("status");
  if (statusRaw && PAYMENT_STATUSES.has(statusRaw)) {
    return [statusRaw as APITypes.PaymentStatusType];
  }

  return undefined;
}

export function hasExplicitStatusFilter(
  searchParams: URLSearchParams,
): boolean {
  return searchParams.has("statuses") || searchParams.has("status");
}

export function buildDefaultTransactionSearch(
  searchParams: URLSearchParams,
): string {
  const next = new URLSearchParams(searchParams);
  if (!hasExplicitStatusFilter(next)) {
    next.set("statuses", DEFAULT_TRANSACTION_STATUSES.join(","));
  }
  if (!next.has("limit")) {
    next.set("limit", String(DEFAULT_TRANSACTION_TABLE_LIMIT));
  }
  next.delete("nextToken");
  return next.toString();
}

export function parseTransactionListFilters(
  searchParams: URLSearchParams,
): TransactionListFiltersType {
  const filters: TransactionListFiltersType = {};

  const month = parseOptionalInt(searchParams.get("month"), 0, 11);
  const year = parseOptionalInt(searchParams.get("year"), 2000, 2100);
  if (month !== undefined) filters.month = month;
  if (year !== undefined) filters.year = year;

  const statuses = parseStatuses(searchParams);
  if (statuses) {
    filters.statuses = statuses;
    if (statuses.length === 1) {
      filters.status = statuses[0];
    }
  }

  const reasonRaw = searchParams.get("reason");
  if (reasonRaw && TRANSACTION_REASONS.has(reasonRaw)) {
    filters.reason = reasonRaw as APITypes.TransactionReason;
  }

  const tenantID = trimId(searchParams.get("tenantID"));
  if (tenantID) filters.tenantID = tenantID;

  const unitID = trimId(searchParams.get("unitID"));
  const buildingID = trimId(searchParams.get("buildingID"));
  const propertyID =
    trimId(searchParams.get("propertyID")) ??
    trimId(searchParams.get("propertyId"));

  if (unitID) {
    filters.unitID = unitID;
  } else if (buildingID) {
    filters.buildingID = buildingID;
  } else if (propertyID) {
    filters.propertyID = propertyID;
  }

  const limit = parseOptionalInt(searchParams.get("limit"), 1, 500);
  filters.limit = limit ?? DEFAULT_TRANSACTION_TABLE_LIMIT;

  const nextToken = trimId(searchParams.get("nextToken"));
  if (nextToken) filters.nextToken = nextToken;

  return filters;
}

/** Server applies unit > building > property; only one entity scope is active. */
export function pickEffectiveEntityFilters(
  values: Pick<
    TransactionListFiltersType,
    "propertyID" | "buildingID" | "unitID"
  >,
): Pick<TransactionListFiltersType, "propertyID" | "buildingID" | "unitID"> {
  if (values.unitID) {
    return { unitID: values.unitID };
  }
  if (values.buildingID) {
    return { buildingID: values.buildingID };
  }
  if (values.propertyID) {
    return { propertyID: values.propertyID };
  }
  return {};
}

export function transactionFiltersToLoaderParams(
  filters: TransactionListFiltersType,
  base: { ownerID: string; ownerType: APITypes.OwnerType; userID: string },
): Record<string, string | undefined> {
  return {
    ownerID: base.ownerID,
    ownerType: base.ownerType,
    userID: base.userID,
    statuses:
      filters.statuses && filters.statuses.length > 0
        ? filters.statuses.join(",")
        : undefined,
    tenantID: filters.tenantID,
    reason: filters.reason,
    month:
      filters.month === undefined ? undefined : String(filters.month),
    year: filters.year === undefined ? undefined : String(filters.year),
    propertyID: filters.propertyID,
    buildingID: filters.buildingID,
    unitID: filters.unitID,
    limit: filters.limit === undefined ? undefined : String(filters.limit),
    nextToken: filters.nextToken,
  };
}
